import { useFavoriteTab } from '../../hooks/useFavoriteTab';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { TanTable } from '../../components/TanTable';
import { ColumnDef } from '@tanstack/react-table';
import { Drawer } from '../../components/ui/Drawer';
import Notification from '../../components/Notification';
import { PencilIcon, TrashIcon, MapIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import roadmapService, {
  RoadmapSection,
  RoadmapItem,
  CreateRoadmapItemRequest,
  UpdateRoadmapItemRequest,
} from '../../services/roadmap.service';
import { MONTH_ABBREVS, monthAt, monthIndex, roadmapYears } from '../../utils/roadmapTimeline';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const THIS_YEAR = new Date().getFullYear();
const MONTH_COL_MIN_WIDTH = 56; // px per month column before the timeline scrolls sideways
const LABEL_COL_WIDTH = 200; // px
const SECTIONS = [
  'GWI 360 OPERATIONS',
  'GWIMOVE DRIVER APP',
  'GWITRACK — CUSTOMER APP',
  'AI / INTEGRATIONS',
  'BACKLOG',
];
const SECTION_COLORS: Record<string, string> = {
  'GWI 360 OPERATIONS': '#5b9fff',
  'GWIMOVE DRIVER APP': '#22c97a',
  'GWITRACK — CUSTOMER APP': '#f5c842',
  'AI / INTEGRATIONS': '#c084fc',
  'BACKLOG': '#9ca3af',
};
const SECTION_ORDERS: Record<string, number> = {
  'GWI 360 OPERATIONS': 0,
  'GWIMOVE DRIVER APP': 1,
  'GWITRACK — CUSTOMER APP': 2,
  'AI / INTEGRATIONS': 3,
  'BACKLOG': 4,
};

const STATUS_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  live: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', border: 'border-green-300 dark:border-green-700' },
  progress: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400', border: 'border-blue-300 dark:border-blue-700' },
  planned: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400', border: 'border-amber-300 dark:border-amber-700' },
  tentative: { bg: 'bg-gray-100 dark:bg-gray-900/30', text: 'text-gray-700 dark:text-gray-400', border: 'border-gray-300 dark:border-gray-700' },
};

const BAR_COLORS: Record<string, { bg: string; border: string; dashed?: boolean }> = {
  live: { bg: '#22c55e', border: 'rgb(var(--c-green-600))' },
  progress: { bg: '#3b82f6', border: 'rgb(var(--c-blue-600))' },
  planned: { bg: '#f59e0b', border: 'rgb(var(--c-amber-600))' },
  tentative: { bg: '#9ca3af', border: 'rgb(var(--c-gray-500))', dashed: true },
};

interface FormData {
  name: string;
  section_name: string;
  section_color: string;
  section_order: number;
  status: string;
  start_year: number;
  start_month: number;
  end_year: number;
  end_month: number;
  description: string;
  timeline_label: string;
  is_sub_feature: boolean;
  parent_id: number | null;
  sort_order: number;
}

const DEFAULT_FORM: FormData = {
  name: '',
  section_name: SECTIONS[0],
  section_color: '#5b9fff',
  section_order: 0,
  status: 'planned',
  start_year: THIS_YEAR,
  start_month: 1,
  end_year: THIS_YEAR,
  end_month: 1,
  description: '',
  timeline_label: '',
  is_sub_feature: false,
  parent_id: null,
  sort_order: 0,
};

/** Drag positions are absolute 0-based month columns counted from January of the first year shown. */
interface DragState {
  itemId: number;
  mode: 'move' | 'resize-left' | 'resize-right';
  startX: number;
  origStart: number;
  origEnd: number;
}

interface DragPreview {
  start: number;
  end: number;
}

interface NotificationState {
  type: 'success' | 'error';
  title: string;
  message: string;
}

type TabKey = 'data' | 'timeline';

const spanLabelText = (year: number, month: number) => `${MONTH_NAMES[month - 1] || '-'} ${year}`;

const ManageRoadmap: React.FC = () => {
  // --- Data state ---
  const [sections, setSections] = useState<RoadmapSection[]>([]);
  const [loading, setLoading] = useState(true);

  // --- Add drawer state ---
  const [addDrawerOpen, setAddDrawerOpen] = useState(false);
  const [addForm, setAddForm] = useState<FormData>({ ...DEFAULT_FORM });
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // --- Edit drawer state ---
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<RoadmapItem | null>(null);
  const [editForm, setEditForm] = useState<FormData>({ ...DEFAULT_FORM });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // --- Notification state ---
  const [notification, setNotification] = useState<NotificationState | null>(null);

  // --- Active tab ---
  const [activeTab, setActiveTab] = useState<TabKey>('data');
  useFavoriteTab(activeTab, setActiveTab, ['data', 'timeline']);

  // --- Gantt drag state ---
  const [dragState, setDragState] = useState<DragState | null>(null);
  const [dragPreview, setDragPreview] = useState<DragPreview | null>(null);
  const monthColRef = useRef<HTMLDivElement | null>(null);
  const [colWidth, setColWidth] = useState(0);

  // --- Flat items derived from sections ---
  const flatItems = useMemo(() => sections.flatMap((s) => s.rows), [sections]);

  // --- Years on the timeline: this year and next, widened to whatever the items touch ---
  const years = useMemo(() => roadmapYears(flatItems), [flatItems]);
  const firstYear = years[0];
  const totalMonths = years.length * 12;

  // --- Fetch data ---
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await roadmapService.getAll();
      setSections(data);
    } catch (err) {
      setNotification({ type: 'error', title: 'Load Failed', message: 'Failed to load roadmap data.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // --- Measure column width ---
  useEffect(() => {
    const measure = () => {
      if (monthColRef.current) {
        setColWidth(monthColRef.current.getBoundingClientRect().width);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [sections]);

  // --- Gantt drag handlers ---
  useEffect(() => {
    if (!dragState) return;
    const lastCol = totalMonths - 1;

    const handleMouseMove = (e: MouseEvent) => {
      if (!dragState || colWidth === 0) return;
      const deltaMonths = Math.round((e.clientX - dragState.startX) / colWidth);

      let newStart = dragState.origStart;
      let newEnd = dragState.origEnd;

      if (dragState.mode === 'move') {
        newStart = dragState.origStart + deltaMonths;
        newEnd = dragState.origEnd + deltaMonths;
        const span = dragState.origEnd - dragState.origStart;
        if (newStart < 0) {
          newStart = 0;
          newEnd = span;
        }
        if (newEnd > lastCol) {
          newEnd = lastCol;
          newStart = lastCol - span;
        }
      } else if (dragState.mode === 'resize-left') {
        newStart = dragState.origStart + deltaMonths;
        if (newStart < 0) newStart = 0;
        if (newStart > newEnd) newStart = newEnd;
      } else if (dragState.mode === 'resize-right') {
        newEnd = dragState.origEnd + deltaMonths;
        if (newEnd > lastCol) newEnd = lastCol;
        if (newEnd < newStart) newEnd = newStart;
      }

      setDragPreview({ start: newStart, end: newEnd });
    };

    const handleMouseUp = async () => {
      if (dragState && dragPreview) {
        const changed = dragPreview.start !== dragState.origStart || dragPreview.end !== dragState.origEnd;
        if (changed) {
          const start = monthAt(dragPreview.start, firstYear);
          const end = monthAt(dragPreview.end, firstYear);
          try {
            await roadmapService.updatePosition(dragState.itemId, {
              start_year: start.year,
              start_month: start.month,
              end_year: end.year,
              end_month: end.month,
            });
            await fetchData();
          } catch {
            setNotification({ type: 'error', title: 'Update Failed', message: 'Failed to update item position.' });
          }
        }
      }
      setDragState(null);
      setDragPreview(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragState, dragPreview, colWidth, fetchData, firstYear, totalMonths]);

  // --- Add handlers ---
  const handleOpenAddDrawer = useCallback(() => {
    setAddForm({ ...DEFAULT_FORM });
    setAddError(null);
    setAddDrawerOpen(true);
  }, []);

  const handleAddFormChange = useCallback(
    (field: keyof FormData, value: string | number | boolean | null) => {
      setAddForm((prev) => {
        const updated = { ...prev, [field]: value } as FormData;
        if (field === 'section_name') {
          updated.section_color = SECTION_COLORS[value as string] || '#5b9fff';
          updated.section_order = SECTION_ORDERS[value as string] ?? 0;
          // Section change invalidates any selected parent
          updated.parent_id = null;
        }
        if (field === 'is_sub_feature' && value === false) {
          updated.parent_id = null;
        }
        if (field === 'parent_id' && value !== null) {
          const parent = flatItems.find((it) => it.id === value);
          if (parent) {
            updated.section_name = parent.section_name;
            updated.section_color = parent.section_color;
            updated.section_order = parent.section_order;
          }
        }
        return updated;
      });
    },
    [flatItems]
  );

  const spanIsBackwards = (form: FormData) =>
    form.end_year * 12 + form.end_month < form.start_year * 12 + form.start_month;

  const handleCreate = useCallback(async () => {
    if (!addForm.name.trim()) {
      setAddError('Name is required.');
      return;
    }
    if (addForm.is_sub_feature && addForm.parent_id === null) {
      setAddError('Please select a parent feature.');
      return;
    }
    if (spanIsBackwards(addForm)) {
      setAddError('End must be on or after Start.');
      return;
    }
    setAddSaving(true);
    setAddError(null);
    try {
      const req: CreateRoadmapItemRequest = {
        name: addForm.name,
        section_name: addForm.section_name,
        section_color: addForm.section_color,
        section_order: addForm.section_order,
        status: addForm.status,
        start_year: addForm.start_year,
        start_month: addForm.start_month,
        end_year: addForm.end_year,
        end_month: addForm.end_month,
        description: addForm.description,
        timeline_label: addForm.timeline_label,
        parent_id: addForm.is_sub_feature ? addForm.parent_id : null,
        sort_order: addForm.sort_order,
      };
      await roadmapService.create(req);
      await fetchData();
      setNotification({ type: 'success', title: 'Created', message: `"${addForm.name}" has been added.` });
      setAddDrawerOpen(false);
    } catch {
      setAddError('Failed to create item. Please try again.');
    } finally {
      setAddSaving(false);
    }
  }, [addForm, fetchData]);

  // --- Edit handlers ---
  const handleOpenEditDrawer = useCallback((item: RoadmapItem) => {
    setEditingItem(item);
    setEditForm({
      name: item.name,
      section_name: item.section_name,
      section_color: item.section_color,
      section_order: item.section_order,
      status: item.status,
      start_year: item.start_year,
      start_month: item.start_month,
      end_year: item.end_year,
      end_month: item.end_month,
      description: item.description || '',
      timeline_label: item.timeline_label || '',
      is_sub_feature: item.parent_id !== null,
      parent_id: item.parent_id,
      sort_order: item.sort_order,
    });
    setEditError(null);
    setEditDrawerOpen(true);
  }, []);

  const handleEditFormChange = useCallback(
    (field: keyof FormData, value: string | number | boolean | null) => {
      setEditForm((prev) => {
        const updated = { ...prev, [field]: value } as FormData;
        if (field === 'section_name') {
          updated.section_color = SECTION_COLORS[value as string] || '#5b9fff';
          updated.section_order = SECTION_ORDERS[value as string] ?? 0;
          updated.parent_id = null;
        }
        if (field === 'is_sub_feature' && value === false) {
          updated.parent_id = null;
        }
        if (field === 'parent_id' && value !== null) {
          const parent = flatItems.find((it) => it.id === value);
          if (parent) {
            updated.section_name = parent.section_name;
            updated.section_color = parent.section_color;
            updated.section_order = parent.section_order;
          }
        }
        return updated;
      });
    },
    [flatItems]
  );

  const handleUpdate = useCallback(async () => {
    if (!editingItem) return;
    if (!editForm.name.trim()) {
      setEditError('Name is required.');
      return;
    }
    if (editForm.is_sub_feature && editForm.parent_id === null) {
      setEditError('Please select a parent feature.');
      return;
    }
    if (editForm.is_sub_feature && editForm.parent_id === editingItem.id) {
      setEditError('An item cannot be its own parent.');
      return;
    }
    if (spanIsBackwards(editForm)) {
      setEditError('End must be on or after Start.');
      return;
    }
    setEditSaving(true);
    setEditError(null);
    try {
      const req: UpdateRoadmapItemRequest = {
        name: editForm.name,
        section_name: editForm.section_name,
        section_color: editForm.section_color,
        section_order: editForm.section_order,
        status: editForm.status,
        start_year: editForm.start_year,
        start_month: editForm.start_month,
        end_year: editForm.end_year,
        end_month: editForm.end_month,
        description: editForm.description,
        timeline_label: editForm.timeline_label,
        parent_id: editForm.is_sub_feature ? editForm.parent_id : null,
        sort_order: editForm.sort_order,
      };
      await roadmapService.update(editingItem.id, req);
      await fetchData();
      setNotification({ type: 'success', title: 'Updated', message: `"${editForm.name}" has been updated.` });
      setEditDrawerOpen(false);
    } catch {
      setEditError('Failed to update item. Please try again.');
    } finally {
      setEditSaving(false);
    }
  }, [editingItem, editForm, fetchData]);

  // --- Delete handler ---
  const handleDelete = useCallback(
    async (item: RoadmapItem) => {
      if (!window.confirm(`Are you sure you want to delete "${item.name}"?`)) return;
      try {
        await roadmapService.delete(item.id);
        await fetchData();
        setNotification({ type: 'success', title: 'Deleted', message: `"${item.name}" has been deleted.` });
      } catch {
        setNotification({ type: 'error', title: 'Delete Failed', message: `Failed to delete "${item.name}".` });
      }
    },
    [fetchData]
  );

  // --- Gantt bar mousedown ---
  const handleBarMouseDown = useCallback(
    (e: React.MouseEvent, item: RoadmapItem) => {
      e.preventDefault();
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const barWidth = rect.width;

      let mode: DragState['mode'] = 'move';
      if (offsetX <= 12) {
        mode = 'resize-left';
      } else if (barWidth - offsetX <= 12) {
        mode = 'resize-right';
      }

      const origStart = monthIndex(item.start_year, item.start_month, firstYear);
      const origEnd = monthIndex(item.end_year, item.end_month, firstYear);
      setDragState({
        itemId: item.id,
        mode,
        startX: e.clientX,
        origStart,
        origEnd,
      });
      setDragPreview({ start: origStart, end: origEnd });
    },
    [firstYear]
  );

  // --- Table columns ---
  const columns = useMemo<ColumnDef<RoadmapItem>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        size: 200,
        cell: ({ row }) => (
          <span className="font-medium text-body">{row.original.name}</span>
        ),
      },
      {
        accessorKey: 'section_name',
        header: 'Section',
        size: 180,
        cell: ({ row }) => (
          <span className="text-label">{row.original.section_name}</span>
        ),
      },
      {
        accessorKey: 'description',
        header: 'Description',
        size: 300,
        cell: ({ row }) => (
          <span className="text-gray-600 dark:text-gray-400 truncate block max-w-[300px]" title={row.original.description || ''}>
            {row.original.description || '-'}
          </span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 120,
        cell: ({ row }) => {
          const status = row.original.status;
          const colors = STATUS_COLORS[status] || STATUS_COLORS.planned;
          return (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${colors.bg} ${colors.text} ${colors.border}`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
          );
        },
      },
      {
        id: 'start_month',
        accessorFn: (row) => spanLabelText(row.start_year, row.start_month),
        header: 'Start',
        size: 110,
        sortingFn: (a, b) =>
          (a.original.start_year * 12 + a.original.start_month) - (b.original.start_year * 12 + b.original.start_month),
        cell: ({ row }) => (
          <span className="text-gray-600 dark:text-gray-400">
            {spanLabelText(row.original.start_year, row.original.start_month)}
          </span>
        ),
      },
      {
        id: 'end_month',
        accessorFn: (row) => spanLabelText(row.end_year, row.end_month),
        header: 'End',
        size: 110,
        sortingFn: (a, b) =>
          (a.original.end_year * 12 + a.original.end_month) - (b.original.end_year * 12 + b.original.end_month),
        cell: ({ row }) => (
          <span className="text-gray-600 dark:text-gray-400">
            {spanLabelText(row.original.end_year, row.original.end_month)}
          </span>
        ),
      },
      {
        accessorKey: 'parent_id',
        header: 'Sub-feature',
        size: 80,
        cell: ({ row }) =>
          row.original.parent_id !== null ? (
            <CheckIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
          ) : null,
      },
      {
        id: 'actions',
        header: 'Actions',
        size: 100,
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleOpenEditDrawer(row.original)}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-muted hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              title="Edit"
            >
              <PencilIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => handleDelete(row.original)}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-muted hover:text-red-600 dark:hover:text-red-400 transition-colors"
              title="Delete"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        ),
      },
    ],
    [handleOpenEditDrawer, handleDelete]
  );

  // --- Form renderer (shared between add/edit) ---
  const renderForm = (
    form: FormData,
    onChange: (field: keyof FormData, value: string | number | boolean | null) => void,
    error: string | null,
    editingId?: number
  ) => {
    const parentCandidates = flatItems.filter(
      (it) =>
        it.parent_id === null &&
        it.section_name === form.section_name &&
        it.id !== editingId
    );

    // The years already on the chart, plus whatever this item is set to so an
    // out-of-range value never vanishes from its own dropdown.
    const yearOptions = Array.from(new Set([...years, form.start_year, form.end_year])).sort((a, b) => a - b);

    const selectClass = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-panel text-body focus:ring-blue-500 focus:border-blue-500';

    const renderMonthYear = (label: string, monthField: 'start_month' | 'end_month', yearField: 'start_year' | 'end_year') => (
      <div>
        <label className="block text-sm font-medium text-label mb-1">{label}</label>
        <div className="grid grid-cols-2 gap-2">
          <select
            aria-label={`${label} month`}
            value={form[monthField]}
            onChange={(e) => onChange(monthField, parseInt(e.target.value))}
            className={selectClass}
          >
            {MONTH_NAMES.map((m, i) => (
              <option key={i} value={i + 1}>
                {m}
              </option>
            ))}
          </select>
          <select
            aria-label={`${label} year`}
            value={form[yearField]}
            onChange={(e) => onChange(yearField, parseInt(e.target.value))}
            className={selectClass}
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      </div>
    );

    return (
    <div className="space-y-4 p-4">
      {error && (
        <div className="p-3 rounded-md bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm">
          {error}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-label mb-1">
          Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={form.name}
          onChange={(e) => onChange('name', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-panel text-body focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-label mb-1">
          Section
          {form.is_sub_feature && (
            <span className="ml-2 text-xs font-normal text-muted">
              (inherited from parent)
            </span>
          )}
        </label>
        <select
          value={form.section_name}
          onChange={(e) => onChange('section_name', e.target.value)}
          disabled={form.is_sub_feature}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-panel text-body focus:ring-blue-500 focus:border-blue-500 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {SECTIONS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-label mb-1">Section Color</label>
        <input
          type="text"
          value={form.section_color}
          onChange={(e) => onChange('section_color', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-panel text-body focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-label mb-1">Section Order</label>
        <input
          type="number"
          value={form.section_order}
          onChange={(e) => onChange('section_order', parseInt(e.target.value) || 0)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-panel text-body focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-label mb-1">Status</label>
        <select
          value={form.status}
          onChange={(e) => onChange('status', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-panel text-body focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="live">Live</option>
          <option value="progress">In Progress</option>
          <option value="planned">Planned</option>
          <option value="tentative">Tentative</option>
        </select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {renderMonthYear('Start', 'start_month', 'start_year')}
        {renderMonthYear('End', 'end_month', 'end_year')}
      </div>

      <div>
        <label className="block text-sm font-medium text-label mb-1">Description</label>
        <textarea
          value={form.description}
          onChange={(e) => onChange('description', e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-panel text-body focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-label mb-1">Timeline Label</label>
        <input
          type="text"
          value={form.timeline_label}
          onChange={(e) => onChange('timeline_label', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-panel text-body focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="is_sub_feature"
          checked={form.is_sub_feature}
          onChange={(e) => onChange('is_sub_feature', e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
        />
        <label htmlFor="is_sub_feature" className="text-sm font-medium text-label">
          Sub-feature
        </label>
      </div>

      {form.is_sub_feature && (
        <div>
          <label className="block text-sm font-medium text-label mb-1">
            Parent Feature <span className="text-red-500">*</span>
          </label>
          <select
            value={form.parent_id ?? ''}
            onChange={(e) =>
              onChange('parent_id', e.target.value === '' ? null : parseInt(e.target.value, 10))
            }
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-panel text-body focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">-- Select a parent --</option>
            {parentCandidates.map((p) => (
              <option key={p.id} value={p.id}>
                [{p.section_name}] {p.name}
              </option>
            ))}
          </select>
          {parentCandidates.length === 0 && (
            <p className="mt-1 text-xs text-muted">
              No eligible top-level features in this section.
            </p>
          )}
        </div>
      )}
    </div>
    );
  };

  // --- Gantt chart rendering ---
  const renderGanttChart = () => {
    const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
    const chartMinWidth = LABEL_COL_WIDTH + totalMonths * MONTH_COL_MIN_WIDTH;

    return (
      <div className="bg-panel rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
        {/* Section title */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-body">Interactive Timeline</h3>
          <p className="text-sm text-muted mt-1">
            Drag bars to move, drag edges to resize
          </p>
        </div>

        <div className="overflow-x-auto">
          <div style={{ minWidth: `${chartMinWidth}px` }}>
            {/* Year header */}
            <div className="flex">
              <div className="shrink-0" style={{ width: `${LABEL_COL_WIDTH}px` }} />
              <div className="flex-1 flex">
                {years.map((year) => (
                  <div
                    key={year}
                    className="flex-1 text-center py-1.5 text-xs font-bold tracking-widest text-white bg-slate-900 dark:bg-slate-800 border-l border-white/10 first:border-l-0"
                  >
                    {year}
                  </div>
                ))}
              </div>
            </div>

            {/* Quarter header — repeats once per year */}
            <div className="flex">
              <div className="shrink-0" style={{ width: `${LABEL_COL_WIDTH}px` }} />
              <div className="flex-1 flex">
                {years.map((year) => quarters.map((q) => (
                  <div
                    key={`${year}-${q}`}
                    className="flex-1 text-center py-2 text-sm font-bold text-white bg-slate-800 dark:bg-slate-700 border-l border-white/10 first:border-l-0"
                  >
                    {q}
                  </div>
                )))}
              </div>
            </div>

            {/* Month header */}
            <div className="flex border-b border-gray-200 dark:border-gray-700">
              <div className="shrink-0" style={{ width: `${LABEL_COL_WIDTH}px` }} />
              <div className="flex-1 flex">
                {years.map((year) => MONTH_ABBREVS.map((m, i) => (
                  <div
                    key={`${year}-${m}`}
                    ref={year === firstYear && i === 0 ? monthColRef : undefined}
                    className="flex-1 text-center py-1.5 text-xs font-medium text-muted border-l border-gray-100 dark:border-gray-700"
                  >
                    {m}
                  </div>
                )))}
              </div>
            </div>

            {/* Section groups and items */}
            {sections.map((section) => (
              <div key={section.section}>
                {/* Section header */}
                <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 dark:bg-gray-750 border-b border-gray-200 dark:border-gray-700">
                  <div
                    className="w-3 h-3 rounded-full shrink-0"
                    style={{ backgroundColor: section.section_color }}
                  />
                  <span className="text-sm font-semibold text-label">
                    {section.section}
                  </span>
                </div>

                {/* Item rows */}
                {section.rows.map((item) => {
                  const isDragging = dragState?.itemId === item.id;
                  const displayStart = isDragging && dragPreview
                    ? dragPreview.start
                    : monthIndex(item.start_year, item.start_month, firstYear);
                  const displayEnd = isDragging && dragPreview
                    ? dragPreview.end
                    : monthIndex(item.end_year, item.end_month, firstYear);
                  const barColor = BAR_COLORS[item.status] || BAR_COLORS.planned;

                  const leftPercent = (displayStart / totalMonths) * 100;
                  const widthPercent = ((displayEnd - displayStart + 1) / totalMonths) * 100;

                  return (
                    <div
                      key={item.id}
                      className="flex items-center border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-750/50 transition-colors"
                    >
                      {/* Label */}
                      <div className="shrink-0 px-4 py-2" style={{ width: `${LABEL_COL_WIDTH}px` }}>
                        <span
                          className={`text-sm ${
                            item.parent_id !== null
                              ? 'text-muted pl-4'
                              : 'text-label font-medium'
                          }`}
                        >
                          {item.parent_id !== null && '- '}
                          {item.timeline_label || item.name}
                        </span>
                      </div>

                      {/* Timeline area */}
                      <div className="flex-1 relative h-10">
                        {/* Grid lines — a heavier rule where a new year begins */}
                        {Array.from({ length: totalMonths }, (_, i) => (
                          <div
                            key={i}
                            className={`absolute top-0 bottom-0 border-l ${
                              i > 0 && i % 12 === 0
                                ? 'border-gray-300 dark:border-gray-600'
                                : 'border-gray-100 dark:border-gray-700/30'
                            }`}
                            style={{ left: `${(i / totalMonths) * 100}%` }}
                          />
                        ))}

                        {/* Bar */}
                        <div
                          className="absolute top-1.5 bottom-1.5 rounded-md flex items-center justify-center text-xs font-medium text-white select-none"
                          style={{
                            left: `${leftPercent}%`,
                            width: `${widthPercent}%`,
                            backgroundColor: barColor.bg,
                            border: `2px ${barColor.dashed ? 'dashed' : 'solid'} ${barColor.border}`,
                            cursor: isDragging ? 'grabbing' : 'grab',
                            zIndex: isDragging ? 10 : 1,
                            opacity: isDragging ? 0.85 : 1,
                          }}
                          onMouseDown={(e) => handleBarMouseDown(e, item)}
                          onMouseMove={(e) => {
                            if (dragState) return;
                            const rect = e.currentTarget.getBoundingClientRect();
                            const offsetX = e.clientX - rect.left;
                            const barWidth = rect.width;
                            if (offsetX <= 12 || barWidth - offsetX <= 12) {
                              e.currentTarget.style.cursor = 'ew-resize';
                            } else {
                              e.currentTarget.style.cursor = isDragging ? 'grabbing' : 'grab';
                            }
                          }}
                        >
                          {/* Left resize handle */}
                          <div className="absolute left-0 top-0 bottom-0 w-3 cursor-ew-resize opacity-0 hover:opacity-100 flex items-center justify-center">
                            <div className="w-0.5 h-3 bg-current rounded opacity-40" />
                          </div>
                          <span className="truncate px-4 text-[0.6875rem]">
                            {item.timeline_label || item.name}
                          </span>
                          {/* Right resize handle */}
                          <div className="absolute right-0 top-0 bottom-0 w-3 cursor-ew-resize opacity-0 hover:opacity-100 flex items-center justify-center">
                            <div className="w-0.5 h-3 bg-current rounded opacity-40" />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}

            {/* Empty state */}
            {sections.length === 0 && !loading && (
              <div className="py-12 text-center text-gray-400 dark:text-gray-500 text-sm">
                No roadmap items yet. Add items using the table above.
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* Notification */}
      {notification && (
        <Notification
          type={notification.type}
          title={notification.title}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      {/* Page header */}
      <div className="flex items-center gap-3">
        <MapIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
        <div>
          <h1 className="text-2xl font-bold text-body">Manage Roadmap</h1>
          <p className="text-sm text-muted">
            Create and manage product roadmap items and timeline
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700">
        <TabButton active={activeTab === 'data'} onClick={() => setActiveTab('data')}>
          Road Map Data
        </TabButton>
        <TabButton active={activeTab === 'timeline'} onClick={() => setActiveTab('timeline')}>
          Timeline
        </TabButton>
      </div>

      {activeTab === 'data' && (
        <TanTable
          data={flatItems}
          columns={columns}
          loading={loading}
          cardTitle="Roadmap Items"
          showEditFiltersButton={true}
          editFiltersButtonIcon="plus"
          editFiltersButtonTooltip="Add Roadmap Item"
          onEditFilters={handleOpenAddDrawer}
          settingsKey="manage-roadmap-table"
          enableColumnFilters={true}
          lockedColumns={[{ id: 'actions', side: 'right' }]}
        />
      )}

      {activeTab === 'timeline' && renderGanttChart()}

      {/* Add Drawer */}
      <Drawer
        isOpen={addDrawerOpen}
        onClose={() => setAddDrawerOpen(false)}
        title="Add Roadmap Item"
        position="right"
        maxWidth="md"
        footer={
          <div className="flex justify-end gap-3 px-4 py-3">
            <button
              onClick={() => setAddDrawerOpen(false)}
              className="px-4 py-2 text-sm font-medium text-label bg-panel border border-gray-300 dark:border-gray-600 rounded-md hover:bg-highlight transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={addSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {addSaving ? 'Creating...' : 'Create'}
            </button>
          </div>
        }
      >
        {renderForm(addForm, handleAddFormChange, addError)}
      </Drawer>

      {/* Edit Drawer */}
      <Drawer
        isOpen={editDrawerOpen}
        onClose={() => setEditDrawerOpen(false)}
        title="Edit Roadmap Item"
        position="right"
        maxWidth="md"
        footer={
          <div className="flex justify-end gap-3 px-4 py-3">
            <button
              onClick={() => setEditDrawerOpen(false)}
              className="px-4 py-2 text-sm font-medium text-label bg-panel border border-gray-300 dark:border-gray-600 rounded-md hover:bg-highlight transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleUpdate}
              disabled={editSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {editSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        }
      >
        {renderForm(editForm, handleEditFormChange, editError, editingItem?.id)}
      </Drawer>
    </div>
  );
};

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={
        'px-4 py-2 text-sm font-medium rounded-t-md border-b-2 transition-colors ' +
        (active
          ? 'border-blue-500 text-body'
          : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200')
      }
    >
      {children}
    </button>
  );
}

export default ManageRoadmap;
